import { FilterNbAirport, SearchResponse } from '@/src/types/flight'
import { encryptQueryString } from '@/src/utils/crypto/encryption'
import setCommonHeaders from '@/src/utils/rtkQuery/setCommonHeaders'
import {
    airlineFilterPriceModifier,
    priceRangeFilterPriceModifier,
    stopsFilterPriceModifier
} from '@/src/utils/srp/RT/filter-price-modier'
import { modifyPricesAndSortOnwardLeg } from '@/src/utils/srp/RT/sort-modifier'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

interface SrpResultsArgs {
    query: string
}

const srpFlightResultsApi = createApi({
    reducerPath: 'srpFlightResultsApi',
    baseQuery: fetchBaseQuery({
        prepareHeaders: headers => {
            headers.set('x-multi-airline', 'true')
            headers.set('x-time-zone', Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone)
            return setCommonHeaders(headers)
        }
    }),
    endpoints: builder => ({
        getSrpResults: builder.query<SearchResponse, SrpResultsArgs>({
            query: ({ query }) => {
                const encryptedData = encryptQueryString(query)
                return `/flight/search/v2/results?data=${encryptedData}`
            },
            transformResponse: (response: SearchResponse, _meta, arg) => {
                if (!response) return response

                const isRT = response?.cards?.length === 2
                response.solutionFlowSelectedLeg = 0

                const rawNbFilter = (response.filterData?.nbAirportFilter ?? []) as unknown as FilterNbAirport[]
                const searchParams = new URLSearchParams(arg?.query || '')
                const searchFrom = searchParams.get('from')?.toUpperCase() || ''
                const searchTo = searchParams.get('to')?.toUpperCase() || ''
                const deepCopyGroups = (groups: FilterNbAirport[]): FilterNbAirport[] =>
                    groups.map(g => ({ ...g, options: [...g.options], selectedValues: [...g.selectedValues] }))
                const buildEmptyGroup = (airportCode: string): FilterNbAirport => ({
                    airportCode,
                    options: [],
                    selectedValues: []
                })
                const normalizeNbGroups = (
                    groups: FilterNbAirport[],
                    departureCode: string,
                    arrivalCode: string
                ): FilterNbAirport[] => {
                    const copied = deepCopyGroups(groups)
                    if (!departureCode || !arrivalCode) return copied

                    const byCode = new Map(copied.map(group => [group.airportCode?.toUpperCase(), group]))
                    const fallbackGroups = copied.filter(
                        group => ![departureCode, arrivalCode].includes(group.airportCode?.toUpperCase())
                    )

                    const departureGroup = byCode.get(departureCode) || fallbackGroups[0] || buildEmptyGroup(departureCode)
                    const arrivalGroup = byCode.get(arrivalCode) || fallbackGroups[1] || buildEmptyGroup(arrivalCode)

                    return [departureGroup, arrivalGroup]
                }

                response.filterData.nbAirportFilter = [normalizeNbGroups(rawNbFilter, searchFrom, searchTo)]
                if (isRT) {
                    response.filterData.nbAirportFilter[1] = normalizeNbGroups(rawNbFilter, searchTo, searchFrom)
                    modifyPricesAndSortOnwardLeg(response)
                    stopsFilterPriceModifier(response, 0)
                    airlineFilterPriceModifier(response, 0)
                    priceRangeFilterPriceModifier(response, 0)
                }

                return response
            }
        })
    })
})

export const { useGetSrpResultsQuery, useLazyGetSrpResultsQuery } = srpFlightResultsApi
export default srpFlightResultsApi
